import React, { useEffect, useMemo, useState } from 'react';
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Container,
    Button,
    FormGroup,
    Label,
    Input,
    Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { SrxApi } from '../api/SrxApi';
import { useHistory, useParams } from 'react-router-dom';
import { DataFetchLoader } from '../components/Loader';
import Swal from 'sweetalert2';
import { RxCross2 } from "react-icons/rx";
import { CiEdit } from "react-icons/ci";
import { hasFacilityPermission } from '../utility-services/rolePermissions';


const FacilitySettings = () => {
    const alertTypes = [
        {
            widget: 'HighCost',
            title: 'High Cost',
            autoResponseActions: [
                { value: 'Approve', label: 'Approve' },
                { value: 'Deny', label: 'Deny' },
                { value: 'ApproveOnce', label: 'Approve Once' },
                { value: 'SubmitLimit', label: 'Approve Limit' },
            ]
        },
        {
            widget: 'HighCostAlerts',
            title: 'High Cost Alerts',
            autoResponseActions: [
                { value: 'Approve', label: 'Approve' },
                { value: 'Deny', label: 'Deny' },
                { value: 'SubmitLimit', label: 'Approve Limit' },
            ]
        },
        {
            widget: 'AlfNonActionOrDenial',
            title: 'ALF High Cost Alerts'
        },
        {
            widget: 'OpenBatch',
            title: 'Clarifications'
        },
        {
            widget: 'NonCovered',
            title: 'Non Covered'
        },
        {
            widget: 'PriorAuth',
            title: 'Prior Authorization'
        },
    ];

    const [loading, setLoading] = useState(false);
    const { facID } = useParams();
    const history = useHistory();
    const [facility, setFacility] = useState(null);
    const [dirtyMap, setDirtyMap] = useState({});
    const [openSections, setOpenSections] = useState({});
    const hasUnsavedChanges = useMemo(() => Object.values(dirtyMap).some(Boolean), [dirtyMap]);

    const handleDirtyChange = (widget, dirty) => {
        setDirtyMap((prev) => {
            if (prev[widget] === dirty) return prev;
            return { ...prev, [widget]: dirty };
        });
    };

    const handleToggleSection = (widget) => {
        setOpenSections((prev) => ({ ...prev, [widget]: !prev[widget] }));
    };

    const getFacility = () => {
        setLoading(true);
        SrxApi.get('/facility/' + facID)
            .then((res) => {
                setFacility(res.data.facility);
                setLoading(false);
            })
            .catch((err) => {
                toast.error('Error: ' + err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        getFacility();
        setLoading(false);
    }, []);

    useEffect(() => {
        const unblock = history.block((nextLocation) => {
            if (!hasUnsavedChanges) return undefined;

            Swal.fire({
                title: 'Unsaved Changes!',
                text: 'You have unsaved changes. Do you want to leave without saving?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Leave',
                cancelButtonText: 'Stay',
                reverseButtons: true,
                focusCancel: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    unblock();
                    history.push(nextLocation.pathname + nextLocation.search + nextLocation.hash);
                }
            });

            return false;
        });

        return () => unblock();
    }, [history, hasUnsavedChanges]);

    return (
        <React.Fragment>
            {loading ?
                <>
                    <DataFetchLoader name="Loading Settings..." />
                </>
                :
                <Container fluid>
                    <h3 className="text-center">
                        Facility Settings for{' '}
                        <Badge style={{ fontSize: '15px' }} className="badge badge-secondary py-1">
                            {facility?.facName}
                        </Badge>
                    </h3>

                    {facility && alertTypes.map((alertType, index) => (
                        <Row key={index}>
                            <AlertSetting
                                facility={facility}
                                alertType={alertType}
                                onSaved={getFacility}
                                onDirtyChange={handleDirtyChange}
                                isOpen={Boolean(openSections[alertType.widget])}
                                onToggle={() => handleToggleSection(alertType.widget)}
                                canView={hasFacilityPermission(facility, 'canViewFacilitySettings')}
                                canEdit={hasFacilityPermission(facility, 'canEditFacilitySettings')}
                            />
                        </Row>
                    ))}
                </Container>
            }
        </React.Fragment>
    );
};
export default FacilitySettings;

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
const DAY_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
const EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE = 'Add at least one email';
const INTERVAL_REQUIRED_FOR_MULTIPLE_REMINDERS_MESSAGE = 'Interval must be greater than 0 when reminders are more than 1.';
function isValidEmail(email) {
    return email && typeof email === 'string' && EMAIL_REGEX.test(email.trim());
}

function formatTimeTo12Hour(timeValue) {
    if (!timeValue) return '--';
    const normalized = String(timeValue).trim();
    const parts = normalized.split(':');
    if (parts.length < 2) return normalized;
    const hourNum = Number(parts[0]);
    const minuteNum = Number(parts[1]);
    if (Number.isNaN(hourNum) || Number.isNaN(minuteNum)) return normalized;
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = ((hourNum + 11) % 12) + 1;
    const displayMinute = String(minuteNum).padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
}

const EmailPillInput = ({ emails, onAdd, onRemove, inputValue, setInputValue }) => {
    const [error, setError] = useState('');

    const addInputEmail = () => {
        const trimmed = (inputValue ?? '').trim();
        if (!trimmed) return false;
        if (!isValidEmail(trimmed)) {
            setError('Please enter a valid email address.');
            return false;
        }
        const exists = emails.some((email) => email.toLowerCase() === trimmed.toLowerCase());
        if (exists) {
            setError('Email already exists.');
            return false;
        }
        setError('');
        onAdd(trimmed);
        setInputValue('');
        return true;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addInputEmail();
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap mb-1">
                {emails.map((email, idx) => (
                    <Badge key={`${email}-${idx}`} color="secondary" className="d-flex align-items-center py-1 px-2 mr-1 mb-1">
                        {email}
                        <span
                            role="button"
                            style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1.1em' }}
                            onClick={() => onRemove(email)}
                        >
                            &times;
                        </span>
                    </Badge>
                ))}
            </div>
            <Input
                type="email"
                placeholder="Enter email and press Enter"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
            />
            {error && <div className="text-danger mt-1" style={{ fontSize: '0.85em' }}>{error}</div>}
        </div>
    );
};


const RecipientWizard = ({ recipientDraft, setRecipientDraft, recipientInputError, setRecipientInputError, onAdd, onCancelEdit, isEditing }) => {
    const [step, setStep] = useState(1);
    const emailValid = isValidEmail(recipientDraft.email.trim());
    const timeError = recipientDraft.fromTime && recipientDraft.toTime && recipientDraft.fromTime >= recipientDraft.toTime;
    const primaryColor = 'var(--bs-primary, #727cf5)';

    useEffect(() => {
        if (isEditing) {
            const hasTime = Boolean(recipientDraft.fromTime || recipientDraft.toTime);
            const hasDays = Boolean(recipientDraft.ActiveDays && recipientDraft.ActiveDays.length > 0);
            setStep(hasDays ? 3 : hasTime ? 2 : 1);
        } else if (!recipientDraft.email && !recipientDraft.fromTime && !recipientDraft.toTime && (!recipientDraft.ActiveDays || recipientDraft.ActiveDays.length === 0)) {
            setStep(1);
        }
    }, [isEditing, recipientDraft.fromTime, recipientDraft.toTime, recipientDraft.ActiveDays, recipientDraft.email]);

    const handleBack = () => setStep((s) => Math.max(1, s - 1));
    const handleNext = () => {
        if (step === 1) {
            if (!emailValid) {
                setRecipientInputError('Please enter a valid email address.');
                return;
            }
            setRecipientInputError('');
        }
        if (step === 2 && timeError) return;
        setStep((s) => Math.min(3, s + 1));
    };

    const handleAddAndReset = () => {
        onAdd();          // calls addRecipientFromInput() in AlertSetting
        setStep(1);       // reset wizard back to step 1
    };

    const stepState = (n) => (step > n ? 'done' : step === n ? 'active' : 'idle');

    /* ── inline styles (no external CSS needed) ── */
    const S = {
        wrapper: { border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', backgroundColor: '#f8f9fa', marginBottom: '8px' },
        stepRow: { display: 'flex', alignItems: 'center', marginBottom: '20px' },
        stepDotWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' },
        stepDot: (state) => ({
            width: 26, height: 26, borderRadius: '50%',
            background: state === 'idle' ? '#dee2e6' : primaryColor,
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
            boxShadow: state === 'active' ? '0 0 0 3px rgba(13,110,253,0.2)' : 'none',
            transition: 'all 0.25s',
        }),
        stepLabel: (state) => ({
            fontSize: 9, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase',
            color: state === 'active' ? primaryColor : state === 'done' ? primaryColor : '#adb5bd',
            transition: 'color 0.25s',
        }),
        stepLine: (done) => ({
            flex: 1, height: 2, marginBottom: 14,
            background: done ? primaryColor : '#dee2e6',
            borderRadius: 2, transition: 'background 0.25s',
        }),
        preview: {
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fff', border: '1px solid #dee2e6',
            borderRadius: 6, padding: '6px 10px', marginBottom: 14, flexWrap: 'wrap',
        },
        previewEmail: { fontSize: 12, color: primaryColor, fontFamily: 'monospace', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        timeBadge: { fontSize: 11, color: '#198754', background: '#d1e7dd', border: '1px solid #a3cfbb', borderRadius: 4, padding: '1px 7px', flexShrink: 0 },
        changeBtn: { fontSize: 11, fontWeight: 700, color: '#0d6efd', background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer' },
        hint: { fontSize: 12, color: '#6c757d', marginBottom: 12 },
        errMsg: { fontSize: 12, color: '#dc3545', marginTop: 4 },
        quickRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
        quickBtn: { fontSize: 11, fontWeight: 600, color: '#0d6efd', background: '#e7f1ff', border: '1px solid #b6d4fe', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' },
        daysGrid: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
        selInfo: { fontSize: 11, color: '#6c757d', background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 10px', marginBottom: 12 },
        btnRow: { display: 'flex', gap: 8 },
    };

    const Checkmark = () => (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
    const ChevronRight = () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
    const ChevronLeft = () => (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );

    return (
        <div style={S.wrapper}>
            {/* ── Step indicator ── */}
            <div style={S.stepRow}>
                {['EMAIL', 'TIME', 'DAYS'].map((label, i) => {
                    const n = i + 1;
                    const state = stepState(n);
                    return (
                        <React.Fragment key={label}>
                            <div style={S.stepDotWrap}>
                                <div style={S.stepDot(state)}>
                                    {state === 'done' ? <Checkmark /> : n}
                                </div>
                                <span style={S.stepLabel(state)}>{label}</span>
                            </div>
                            {i < 2 && <div style={S.stepLine(step > n)} />}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* ── Step 1: Email ── */}
            {step === 1 && (
                <div>
                    <FormGroup className="mb-2">
                        <Label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Recipient Email</Label>
                        <Input
                            type="email"
                            placeholder="name@example.com"
                            value={recipientDraft.email}
                            autoFocus
                            onChange={(e) => {
                                const value = e.target.value;
                                setRecipientDraft((prev) => ({ ...prev, email: value }));
                                if (recipientInputError) setRecipientInputError('');
                            }}
                            onKeyDown={(e) => e.key === 'Enter' }
                            style={{ borderColor: recipientInputError ? '#dc3545' : emailValid ? '#198754' : undefined }}
                        />
                        {recipientInputError && <div style={S.errMsg}>{recipientInputError}</div>}
                    </FormGroup>
                    {/* <div style={S.btnRow}>
                        {isEditing && (
                            <Button color="secondary" size="sm" outline onClick={onCancelEdit}>
                                Cancel Edit
                            </Button>
                        )}
                    </div> */}
                </div>
            )}

            {/* ── Step 2: Time ── */}
            {step === 2 && (
                <div>
                    {/* Preview bar */}
                    <div style={S.preview}>
                        <span style={S.previewEmail}>{recipientDraft.email}</span>
                        <button style={S.changeBtn} onClick={() => setStep(1)}>Change</button>
                    </div>
                    <p style={S.hint}>Set an active notification window.</p>
                    <Row className="mb-2">
                        <Col md={6}>
                            <FormGroup className="mb-1">
                                <Label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>From Time</Label>
                                <Input
                                    type="time"
                                    value={recipientDraft.fromTime || ''}
                                    style={{ borderColor: timeError ? '#dc3545' : undefined }}
                                    onChange={(e) => { 
                                        const value = e.target.value;
                                        setRecipientDraft((prev) => ({ ...prev, fromTime: value }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup className="mb-1">
                                <Label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>To Time</Label>
                                <Input
                                    type="time"
                                    value={recipientDraft.toTime || ''}
                                    style={{ borderColor: timeError ? '#dc3545' : undefined }}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setRecipientDraft((prev) => ({ ...prev, toTime: value }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {timeError && <div style={S.errMsg}>Start time must be before end time.</div>}
                </div>
            )}

            {/* ── Step 3: Active Days ── */}
            {step === 3 && (
                <div>
                    {/* Preview bar */}
                    <div style={S.preview}>
                        <span style={S.previewEmail}>{recipientDraft.email}</span>
                        {(recipientDraft.fromTime || recipientDraft.toTime) && (
                            <span style={S.timeBadge}>
                                {formatTimeTo12Hour(recipientDraft.fromTime)} → {formatTimeTo12Hour(recipientDraft.toTime)}
                            </span>
                        )}
                        <button style={S.changeBtn} onClick={() => setStep(2)}>Edit</button>
                    </div>
                    <p style={S.hint}>Choose which days to send notifications (optional).</p>

                    {/* Quick selects */}
                    <div style={S.quickRow}>
                        {[
                            ['Weekdays', ['Mon', 'Tue', 'Wed', 'Thur', 'Fri']],
                            ['Weekends', ['Sat', 'Sun']],
                            ['Every day', DAY_OPTIONS],
                        ].map(([label, days]) => (
                            <button key={label} style={S.quickBtn}
                                onClick={() => setRecipientDraft((prev) => ({ ...prev, ActiveDays: days }))}>
                                {label}
                            </button>
                        ))}
                        <button style={{ ...S.quickBtn, color: '#6c757d', background: '#f8f9fa', borderColor: '#dee2e6' }}
                            onClick={() => setRecipientDraft((prev) => ({ ...prev, ActiveDays: [] }))}>
                            Clear
                        </button>
                    </div>

                    {/* Day toggle buttons (reuses existing Button from reactstrap) */}
                    <div style={S.daysGrid}>
                        {DAY_OPTIONS.map((day) => {
                            const selected = recipientDraft.ActiveDays.includes(day);
                            return (
                                <Button
                                    key={day}
                                    type="button"
                                    color={selected ? 'primary' : 'secondary'}
                                    size="sm"
                                    style={{ minWidth: 44 }}
                                    onClick={() => {
                                        setRecipientDraft((prev) => {
                                            const exists = prev.ActiveDays.includes(day);
                                            return {
                                                ...prev,
                                                ActiveDays: exists
                                                    ? prev.ActiveDays.filter((d) => d !== day)
                                                    : [...prev.ActiveDays, day],
                                            };
                                        });
                                    }}
                                >
                                    {day}
                                </Button>
                            );
                        })}
                    </div>

                    {/* Selection summary */}
                    <div style={S.selInfo}>
                        {recipientDraft.ActiveDays.length === 0
                            ? 'No days selected — will notify every day'
                            : recipientDraft.ActiveDays.length === DAY_OPTIONS.length
                                ? '✓ Every day selected'
                                : `✓ ${recipientDraft.ActiveDays.join(', ')}`}
                    </div>

                </div>
            )}
            <div style={{ ...S.btnRow, marginTop: 10 }}>
                <Button
                    color="secondary"
                    size="sm"
                    outline
                    className="d-flex align-items-center back-btn"
                    style={{ gap: 4 }}
                    onClick={handleBack}
                    disabled={step === 1}
                >
                    <ChevronLeft /> Back
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    className="d-flex align-items-center"
                    style={{ gap: 6 }}
                    onClick={handleNext}
                    disabled={step === 3 || (step === 2 && !!timeError)}
                >
                    Next <ChevronRight />
                </Button>
                <Button
                    color={isEditing ? 'warning' : 'success'}
                    size="sm"
                    className="d-flex align-items-center"
                    style={{ marginLeft: 'auto', gap: 6 }}
                    onClick={() => {
                        if (!emailValid) {
                            setRecipientInputError('Please enter a valid email address.');
                            return;
                        }
                        if (timeError) return;
                        setRecipientInputError('');
                        handleAddAndReset();
                    }}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {isEditing ? 'Update Recipient' : 'Add Recipient'}
                </Button>
            </div>
        </div>
    );
};


const AlertSetting = (props) => {
    const { facility, alertType, onSaved, onDirtyChange, isOpen, onToggle, canView, canEdit } = props;

    const [emailCount, setEmailCount] = useState(undefined);
    const [emailInterval, setEmailInterval] = useState("");
    const [noResponseAction, setNoResponseAction] = useState("");
    const [limit, setLimit] = useState(1);

    const [emailList, setEmailList] = useState([]);
    const [recipientDraft, setRecipientDraft] = useState({ email: "", fromTime: "", toTime: "", ActiveDays: [] });
    const [recipientInputError, setRecipientInputError] = useState("");
    const [editingRecipientIndex, setEditingRecipientIndex] = useState(null);
    const [saveAllError, setSaveAllError] = useState("");
    const [saving, setSaving] = useState(false);
    const [replyAlertEmails, setReplyAlertEmails] = useState([]);
    const [replyAlertDraftEmail, setReplyAlertDraftEmail] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const initialContactsRef = React.useRef([]);
    const initialValuesRef = React.useRef(null);
    const isInitializingRef = React.useRef(true);

    const normalizeEmailList = (list) => {
        return (list || [])
            .map((e) => ({
                contact: (e?.contactInfo ?? '').trim().toLowerCase(),
                fromTime: (e?.fromTime ?? '').trim(),
                toTime: (e?.toTime ?? '').trim(),
                ActiveDays: String(e?.ActiveDays ?? '')
                    .split(',').map((d) => d.trim()).filter(Boolean)
                    .sort((a, b) => a.localeCompare(b)).join(','),
                id: e?.facilityContactInfoID ?? '',
            }))
            .sort((a, b) => (
                a.contact.localeCompare(b.contact) ||
                a.fromTime.localeCompare(b.fromTime) ||
                a.toTime.localeCompare(b.toTime) ||
                a.ActiveDays.localeCompare(b.ActiveDays) ||
                String(a.id).localeCompare(String(b.id))
            ))
            .map((e) => `${e.contact}||${e.fromTime}||${e.toTime}||${e.ActiveDays}||${e.id}`)
            .join(',');
    };

    const normalizeReplyEmails = (arr) => {
        return (arr || []).map((e) => (e ?? '').trim().toLowerCase()).filter(Boolean)
            .sort((a, b) => a.localeCompare(b)).join(',');
    };

    const getUniqueRecipientsByEmail = (list) => {
        const seen = new Set();
        return (list || []).filter((item) => {
            const key = String(item?.contactInfo ?? '').trim().toLowerCase();
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    useEffect(() => {
        isInitializingRef.current = true;
        setIsDirty(false);

        const nextEmailCount = facility.facilitySettings?.find(s => s.widgetName === alertType.widget && s.settingName == 'EmailNotificationMaxCount')?.settingValue ?? 0;
        const nextEmailInterval = facility.facilitySettings?.find(s => s.widgetName === alertType.widget && s.settingName == 'EmailNotificationIntervalMinutes')?.settingValue ?? 0;
        const nextNoResponseAction = facility.facilitySettings?.find(s => s.widgetName === alertType.widget && s.settingName == 'AutoRespondAction')?.settingValue ?? "";
        const nextLimit = facility?.facilitySettings?.find((s) => s.widgetName === alertType.widget && s.settingName === 'AutoRespondAction' && s.settingValue === 'SubmitLimit')?.settingNote ?? 1;

        const contacts = (facility.facilityContacts || []).filter(c => c.contactMethod === 'Email' && c.widget === alertType.widget);
        const nextEmailList = getUniqueRecipientsByEmail(contacts.map(c => ({
            contactInfo: c.contactInfo,
            facilityContactInfoID: c.facilityContactInfoID,
            fromTime: (c.fromTime ?? '').trim(),
            toTime: (c.toTime ?? '').trim(),
            ActiveDays: String(c.ActiveDays ?? c.activeDays ?? '').trim(),
        })));

        const replyEmailsSetting = facility.facilitySettings?.find((s) => s.widgetName === alertType.widget && s.settingName === 'ReceiveReplyAlert')?.settingValue ?? '';
        const nextReplyAlertEmails = replyEmailsSetting ? replyEmailsSetting.split(',').map((e) => e.trim()).filter(Boolean) : [];

        initialContactsRef.current = nextEmailList;
        initialValuesRef.current = {
            emailCount: String(nextEmailCount ?? ''),
            emailInterval: String(nextEmailInterval ?? ''),
            noResponseAction: String(nextNoResponseAction ?? ''),
            limit: String(nextLimit ?? ''),
            emailList: normalizeEmailList(nextEmailList),
            replyAlertEmails: normalizeReplyEmails(nextReplyAlertEmails),
        };

        setEmailCount(nextEmailCount);
        setEmailInterval(nextEmailInterval);
        setNoResponseAction(nextNoResponseAction);
        setLimit(nextLimit);
        setEmailList(nextEmailList);
        setReplyAlertEmails(nextReplyAlertEmails);
        setReplyAlertDraftEmail('');
        const nextEmailCountValue = Number(nextEmailCount);
        const nextEmailIntervalValue = Number(nextEmailInterval);
        const hasActiveSavedReminderSettings =
            !isNaN(nextEmailCountValue) &&
            Number.isInteger(nextEmailCountValue) &&
            !isNaN(nextEmailIntervalValue) &&
            Number.isInteger(nextEmailIntervalValue) &&
            (nextEmailCountValue > 0 || nextEmailIntervalValue > 0);
        setRecipientInputError(hasActiveSavedReminderSettings && nextEmailList.length === 0 ? EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE : '');

        setTimeout(() => { isInitializingRef.current = false; }, 0);
    }, [facility, alertType.widget]);

    useEffect(() => {
        if (isInitializingRef.current) return;
        const init = initialValuesRef.current;
        if (!init) return;

        const dirty =
            String(emailCount ?? '') !== init.emailCount ||
            String(emailInterval ?? '') !== init.emailInterval ||
            String(noResponseAction ?? '') !== init.noResponseAction ||
            String(limit ?? '') !== init.limit ||
            normalizeEmailList(emailList) !== init.emailList ||
            normalizeReplyEmails(replyAlertEmails) !== init.replyAlertEmails;

        setIsDirty(dirty);
    }, [emailCount, emailInterval, noResponseAction, limit, emailList, replyAlertEmails]);

    useEffect(() => {
        if (onDirtyChange) onDirtyChange(alertType.widget, isDirty);
    }, [onDirtyChange, alertType.widget, isDirty]);

    const emailCountValue = Number(emailCount);
    const emailIntervalValue = Number(emailInterval);
    const hasRecipientEmail = emailList.length > 0;
    const hasValidEmailCount = emailCount !== undefined && emailCount !== "" && !isNaN(emailCountValue) && emailCountValue >= 0 && Number.isInteger(emailCountValue);
    const hasValidEmailInterval = emailInterval !== undefined && emailInterval !== "" && !isNaN(emailIntervalValue) && Number.isInteger(emailIntervalValue) && (emailCountValue <= 1 ? emailIntervalValue >= 0 : emailIntervalValue > 0);
    const intervalInputError = hasValidEmailCount && emailCountValue > 1 && emailInterval !== "" && !isNaN(emailIntervalValue) && Number.isInteger(emailIntervalValue) && emailIntervalValue <= 0
        ? INTERVAL_REQUIRED_FOR_MULTIPLE_REMINDERS_MESSAGE
        : "";
    const remindersRequireRecipientEmail = hasValidEmailCount && hasValidEmailInterval && (emailCountValue > 0 || emailIntervalValue > 0);
    const canSaveEmailSettings = hasValidEmailCount && hasValidEmailInterval && (!remindersRequireRecipientEmail || hasRecipientEmail);
    const isSaveDisabled = saving || !isDirty || !canSaveEmailSettings || !canEdit;

    const showRecipientRequiredError = (nextEmailCount = emailCount, nextEmailInterval = emailInterval) => {
        const nextEmailCountValue = Number(nextEmailCount);
        const nextEmailIntervalValue = Number(nextEmailInterval);
        if (
            nextEmailCount !== undefined &&
            nextEmailCount !== "" &&
            !isNaN(nextEmailCountValue) &&
            Number.isInteger(nextEmailCountValue) &&
            nextEmailInterval !== undefined &&
            nextEmailInterval !== "" &&
            !isNaN(nextEmailIntervalValue) &&
            Number.isInteger(nextEmailIntervalValue) &&
            (nextEmailCountValue > 0 || nextEmailIntervalValue > 0) &&
            emailList.length === 0 &&
            !(recipientDraft.email ?? '').trim()
        ) {
            setRecipientInputError(EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE);
            return;
        }

        if (recipientInputError === EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE) {
            setRecipientInputError("");
        }
    };

    const handleEmailCountChange = (value) => {
        setEmailCount(value);
        showRecipientRequiredError(value);
    };

    const handleEmailIntervalChange = (value) => {
        setEmailInterval(value);
        showRecipientRequiredError(emailCount, value);
    };

    // ── unchanged: addRecipientFromInput ──
    const addRecipientFromInput = () => {
        const email = recipientDraft.email.trim();
        const fromTime = recipientDraft.fromTime;
        const toTime = recipientDraft.toTime;
        const ActiveDays = recipientDraft.ActiveDays.join(',');

        if (!email) { setRecipientInputError('Email is required.'); return; }
        if (!isValidEmail(email)) { setRecipientInputError('Please enter a valid email address.'); return; }

        const already = emailList.some((e, index) => {
            if (editingRecipientIndex !== null && index === editingRecipientIndex) return false;
            return String(e?.contactInfo ?? '').trim().toLowerCase() === email.toLowerCase();
        });
        if (already) { setRecipientInputError('This email already exists in the recipient list.'); return; }
        const nextRecipient = { contactInfo: email, fromTime, toTime, ActiveDays, facilityContactInfoID: undefined };
        if (editingRecipientIndex !== null) {
            setEmailList((prev) => prev.map((item, index) => (index === editingRecipientIndex ? { ...item, ...nextRecipient } : item)));
        } else {
            setEmailList((prev) => [...prev, nextRecipient]);
        }
        setRecipientDraft({ email: "", fromTime: "", toTime: "", ActiveDays: [] });
        setEditingRecipientIndex(null);
        setRecipientInputError("");
    };

    const removeEmail = (index) => {
        setEmailList((prev) => {
            const nextEmailList = prev.filter((_, i) => i !== index);
            if (
                nextEmailList.length === 0 &&
                remindersRequireRecipientEmail &&
                !(recipientDraft.email ?? '').trim()
            ) {
                setRecipientInputError(EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE);
            }
            return nextEmailList;
        });
        setIsDirty(true);
        if (editingRecipientIndex === index) {
            setEditingRecipientIndex(null);
            setRecipientDraft({ email: "", fromTime: "", toTime: "", ActiveDays: [] });
            if (!remindersRequireRecipientEmail) setRecipientInputError("");
        }
    };

    const handleEditRecipient = (item, index) => {
        const activeDaysRaw = String(item?.activeDays ?? item?.ActiveDays ?? '').trim();
        const draftDays = activeDaysRaw ? activeDaysRaw.split(',').map((d) => d.trim()).filter(Boolean) : [];
        setEditingRecipientIndex(index);
        setRecipientDraft({
            email: String(item?.contactInfo ?? '').trim(),
            fromTime: String(item?.fromTime ?? '').trim(),
            toTime: String(item?.toTime ?? '').trim(),
            ActiveDays: draftDays,
        });
        setRecipientInputError("");
    };

    const handleCancelEditRecipient = () => {
        setEditingRecipientIndex(null);
        setRecipientDraft({ email: "", fromTime: "", toTime: "", ActiveDays: [] });
        setRecipientInputError("");
    };

    const validateAll = () => {
        setSaveAllError("");
        if (!hasValidEmailCount) {
            setSaveAllError('Number of Email Reminders must be an integer 0 or greater.'); return false;
        }
        if (!hasValidEmailInterval) {
            setSaveAllError(emailCountValue <= 1
                ? 'Interval between Reminder Emails must be an integer 0 or greater.'
                : 'Interval between Reminder Emails must be an integer greater than 0 when more than 1 reminder is selected.');
            return false;
        }
        if (remindersRequireRecipientEmail && !hasRecipientEmail) {
            setRecipientInputError(EMAIL_REQUIRED_FOR_REMINDERS_MESSAGE);
            setSaveAllError('Add at least one email'); return false;
        }
        if (alertType.autoResponseActions && noResponseAction === 'SubmitLimit') {
            const limitNum = Number(limit);
            if (isNaN(limitNum) || limitNum < 1 || limitNum > 9 || !Number.isInteger(limitNum)) {
                setSaveAllError('Limit Count must be an integer between 1 and 9.'); return false;
            }
        }
        return true;
    };

    const saveAllSettings = async () => {
        if (!validateAll()) return;
        setSaving(true); setSaveAllError("");
        try {
            const pendingReplyEmail = (replyAlertDraftEmail ?? '').trim();
            let finalReplyAlertEmails = [...replyAlertEmails];
            if (pendingReplyEmail) {
                if (!isValidEmail(pendingReplyEmail)) {
                    setSaveAllError('Please enter a valid Receive Reply Alert email.');
                    setSaving(false);
                    return;
                }
                const exists = finalReplyAlertEmails.some((email) => email.toLowerCase() === pendingReplyEmail.toLowerCase());
                if (!exists) {
                    finalReplyAlertEmails.push(pendingReplyEmail);
                }
            }

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const payload = { settingValues: {}, FaciId: facility.facID, WidgetName: alertType.widget, contactInfo: [] };
            payload.settingValues.EmailNotificationMaxCount = String(emailCount);
            payload.settingValues.EmailNotificationIntervalMinutes = String(Number(emailInterval));
            payload.settingValues.ReceiveReplyAlert = finalReplyAlertEmails.join(',');
            if (alertType.autoResponseActions) {
                payload.settingValues.AutoRespondAction = noResponseAction;
                if (noResponseAction === 'SubmitLimit' && limit != null) payload.SettingNote = String(limit);
            }
            payload.contactInfo = emailList.map((e) => ({
                contactMethod: 'Email', contactValue: e.contactInfo,
                fromTime: e.fromTime || null, toTime: e.toTime || null,
                timezone, activeDays: e.ActiveDays,
            }));
            await SrxApi.post('/facilitysetting', payload);
            toast.success('Facility settings for ' + alertType.widget + ' successfully saved');
            setReplyAlertEmails(finalReplyAlertEmails);
            setReplyAlertDraftEmail('');
            setIsDirty(false);
            if (onSaved) onSaved();
        } catch (err) {
            toast.error('Error: ' + (err.message || err));
        } finally {
            setSaving(false);
        }
    };

    const handleAddReplyEmail = (email) => { const u = [...replyAlertEmails, email]; setReplyAlertEmails(u); };
    const handleRemoveReplyEmail = (email) => {
        const u = replyAlertEmails.filter((e) => e !== email);
        setReplyAlertEmails(u);
        setIsDirty(true);
    };

    const effectiveIsOpen = canView && isOpen;

    return (
        <React.Fragment>
            <Col lg={8} xl={7}>
                <Card>
                    <CardHeader
                        className="bg-light"
                        style={{ cursor: canView ? 'pointer' : 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onClick={canView ? onToggle : undefined}
                    >
                        <b>{alertType.title}</b>
                        <i className={`mdi ${effectiveIsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'}`} style={{ fontSize: '20px', transition: 'transform 0.3s ease' }} />
                    </CardHeader>
                    <div style={{ maxHeight: effectiveIsOpen ? '1000px' : '0px', overflow: 'auto', transition: 'max-height 0.3s ease, opacity 0.3s ease', opacity: effectiveIsOpen ? 1 : 0 }}>
                        <CardBody>
                            <Row>
                                <Col md={12}>
                                    <fieldset disabled={!canEdit} style={{ border: 'none', margin: 0, padding: 0 }}>
                                        {/* Number of Email Reminders */}
                                        <div className='mt-2 d-flex align-items-center'>
                                            <FormGroup>
                                                <Label className="mr-2">Number of Email Reminders</Label>
                                                <div className='d-flex'>
                                                    <Input type="number" min={0} step={1} value={emailCount} onChange={(e) => handleEmailCountChange(e.target.value)} style={{ width: '65px' }} className="custom-input" />
                                                </div>
                                            </FormGroup>
                                        </div>

                                        {/* Interval */}
                                        <div className='mt-2 d-flex align-items-center'>
                                            <FormGroup>
                                                <Label className="mr-2">Interval between Reminder Emails (in minutes)</Label>
                                                <div className='d-flex'>
                                                    <Input type="number" value={emailInterval} min={emailCountValue <= 1 ? 0 : 1} step={1} required onChange={(e) => handleEmailIntervalChange(e.target.value)} style={{ width: '65px', borderColor: intervalInputError ? '#dc3545' : undefined }} className="custom-input" />
                                                </div>
                                                {intervalInputError && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{intervalInputError}</div>}
                                            </FormGroup>
                                        </div>

                                        {/* ── Recipient Schedule — now uses the step wizard ── */}
                                        <div className='mt-2'>
                                            <FormGroup>
                                                <Label className="mr-2">Recipient Schedule</Label>

                                                {/* Step-by-step wizard replaces the old flat form */}
                                                <RecipientWizard
                                                    recipientDraft={recipientDraft}
                                                    setRecipientDraft={setRecipientDraft}
                                                    recipientInputError={recipientInputError}
                                                    setRecipientInputError={setRecipientInputError}
                                                    onAdd={addRecipientFromInput}
                                                    onCancelEdit={handleCancelEditRecipient}
                                                    isEditing={editingRecipientIndex !== null}
                                                />

                                                {/* Recipient list table — unchanged */}
                                                    {emailList.length > 0 ? (
                                                        <div className="border rounded p-2" style={{ minHeight: '60px', maxHeight: '180px', overflowY: 'auto' }}>
                                                            <div className="d-none d-md-flex text-muted small border-bottom pb-2 mb-1 font-weight-bold">
                                                                <div style={{ width: '35%' }}>Email</div>
                                                                <div style={{ width: '15%' }}>From Time</div>
                                                                <div style={{ width: '15%' }}>To Time</div>
                                                                <div style={{ width: '25%' }}>Active Days</div>
                                                                <div style={{ width: '10%' }}>Actions</div>
                                                            </div>
                                                            {emailList.map((item, index) => (
                                                                <div
                                                                    key={`${item.facilityContactInfoID ?? 'new'}-${item.contactInfo}-${item.fromTime}-${item.toTime}-${item.ActiveDays}-${index}`}
                                                                    className="d-flex align-items-center justify-content-between border-bottom py-1"
                                                                >
                                                                    <div className="d-flex flex-column flex-md-row align-items-md-center w-100" style={{ gap: '2px' }}>
                                                                        <div style={{ width: '35%' }}>
                                                                            <div style={{color: '#727cf5',fontSize: '14px'}} className="py-1">{item.contactInfo}</div>
                                                                        </div>
                                                                        <div style={{ width: '15%' }} className="small text-dark">{formatTimeTo12Hour(item.fromTime)}</div>
                                                                        <div style={{ width: '15%' }} className="small text-dark">{formatTimeTo12Hour(item.toTime)}</div>
                                                                        <div style={{ width: '25%' }} className="small text-dark">
                                                                            {(item.activeDays || item.ActiveDays) ? (item.activeDays || item.ActiveDays) : '--'}
                                                                        </div>
                                                                        <div style={{ width: '10%', display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                                                            <Button color="link" style={{color: '#727cf5',fontSize: '16px', padding: '0'}} type="button" onClick={() => handleEditRecipient(item, index)}>
                                                                            <CiEdit />
                                                                            </Button>
                                                                            <Button color="link" className="text-danger p-0 small" type="button" onClick={() => removeEmail(index)}>
                                                                                <RxCross2 />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        ""
                                                    )}
                                            </FormGroup>
                                        </div>

                                        {/* Auto Response Action — unchanged */}
                                        {alertType.autoResponseActions && (
                                            <div>
                                                <FormGroup>
                                                    <Label>Auto Response Action</Label>
                                                    <Input type="select" value={noResponseAction} onChange={(e) => setNoResponseAction(e.target.value)}>
                                                        <option value="">No Auto Response Action</option>
                                                        {alertType.autoResponseActions.map((action, index) => (
                                                            <option key={index} value={action.value}>{action.label}</option>
                                                        ))}
                                                    </Input>
                                                    {noResponseAction === 'SubmitLimit' && (
                                                        <div className='mt-4 d-flex align-items-center'>
                                                            <Label className="mr-2">Limit Count</Label>
                                                            <Input type="number" min={1} max={9} value={limit} onChange={(e) => setLimit(e.target.value)} style={{ width: '65px' }} className="custom-input" />
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        )}

                                        {/* Receive Reply Alert — unchanged */}
                                        <div className='mt-3'>
                                            <FormGroup>
                                                <Label className="mb-0 pb-0 ">Receive Reply Alert</Label>
                                                <div style={{ fontSize: '0.85em', fontStyle: 'italic' }} className="text-muted">
                                                    Receive an alert when the pharmacy replies to a question (only for the pharmacy&apos;s first reply)
                                                </div>
                                                <EmailPillInput
                                                    emails={replyAlertEmails}
                                                    onAdd={handleAddReplyEmail}
                                                    onRemove={handleRemoveReplyEmail}
                                                    inputValue={replyAlertDraftEmail}
                                                    setInputValue={setReplyAlertDraftEmail}
                                                />
                                            </FormGroup>
                                        </div>

                                        {saveAllError && <div className="text-danger small mb-2">{saveAllError}</div>}
                                        <Button
                                            color="primary"
                                            onClick={saveAllSettings}
                                            disabled={isSaveDisabled}
                                            style={isSaveDisabled ? {
                                                cursor: 'default',
                                                opacity: 1,
                                                backgroundColor: 'var(--bs-primary, #727cf5)',
                                                borderColor: 'var(--bs-primary, #727cf5)',
                                            } : undefined}
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </Button>
                                    </fieldset>
                                </Col>
                            </Row>
                        </CardBody>
                    </div>
                </Card>
            </Col>
        </React.Fragment>
    );
};
